import { Component, DestroyRef, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DynamicRouteHandlingService, InputFieldComponent } from '@zayan-erp/shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DrawCodesService } from '../../services/draw-codes.service';
import { DrawCode } from '../../models/draw-codes.models';
import { of, tap } from 'rxjs';

@Component({
  selector: 'draw-code-form',
  imports: [ReactiveFormsModule, InputFieldComponent],
  templateUrl: './draw-code-form.component.html',
  styles: [``]
})
export class DrawCodeFormComponent {
  drawCodeService = inject(DrawCodesService);
  activatedRoute = inject(ActivatedRoute);
  dynamicRouteHandling = inject(DynamicRouteHandlingService);
  destroyRef = inject(DestroyRef);
  router = inject(Router);
  modalService = inject(NgbModal);

  constructor() {
    this.dynamicRouteHandling.submitButton$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.onFormSubmit();
    });
  }

  idToEdit = signal<string | null>(this.activatedRoute.snapshot.paramMap.get('id'));

  recordToEditRxResource = rxResourceFixed({
    params: () => ({ id: this.idToEdit() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (params.id) {
        return this.drawCodeService.readDrawCodeById(params.id).pipe(
          tap(drawCode => {
            this.onDataLoaded(drawCode);
          })
        );
      }
      return of(null);
    }
  });

  drawCodeForm = new FormGroup({
    code: new FormControl<string | null>(null, Validators.required),
    description: new FormControl<string | null>(null, Validators.required)
  });

  onDataLoaded(drawCode: DrawCode) {
    this.drawCodeForm.patchValue({
      code: drawCode.code,
      description: drawCode.description
    });
  }

  onFormSubmit(): void {
    if (this.drawCodeForm.invalid) {
      this.drawCodeForm.markAllAsTouched();
      return;
    }

    if (!this.idToEdit()) this.createDrawCode();
    else this.updateDrawCode();
  }

  createDrawCode() {
    const drawCode = this.drawCodeForm.value as unknown as Omit<DrawCode, 'id'>;

    this.drawCodeService.createDrawCode(drawCode).subscribe(() => {
      this.afterFormSubmitSuccess();
    });
  }

  updateDrawCode() {
    const drawCode = this.drawCodeForm.value as unknown as Omit<DrawCode, 'id'>;

    this.drawCodeService.updateDrawCode(drawCode, this.idToEdit()!).subscribe(() => {
      this.afterFormSubmitSuccess();
    });
  }

  afterFormSubmitSuccess() {
    this.dynamicRouteHandling.reload.next(!this.dynamicRouteHandling.reload.value);
    this.modalService.dismissAll();
    this.router.navigate([this.activatedRoute.snapshot.data['returnRoute']], { queryParamsHandling: 'preserve' });
  }
}
